import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  CaretLeft,
  CaretRight,
  Plus,
  X,
  CalendarBlank,
  ListBullets,
  Clock,
  Trash,
  Path,
} from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmt(d) {
  return d.toISOString().slice(0, 10);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export default function Schedule() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [workers, setWorkers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [picker, setPicker] = useState(null); // {workerId, date}
  const [pickerRoute, setPickerRoute] = useState(""); // route_id chosen in picker
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    name: "",
    start_time: "08:00",
    end_time: "16:00",
    color: "#FFDDC1",
  });

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const loadEntries = () => {
    const start = fmt(weekStart);
    const end = fmt(addDays(weekStart, 6));
    api.get(`/schedule?start=${start}&end=${end}`).then((r) => setEntries(r.data));
  };

  useEffect(() => {
    api.get("/workers").then((r) => setWorkers(r.data));
    api.get("/shifts").then((r) => setShifts(r.data));
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, []);
  useEffect(loadEntries, [weekStart]);

  const routeById = (id) => routes.find((r) => r.id === id);

  const shiftById = (id) => shifts.find((s) => s.id === id);

  const cellEntries = (workerId, date) =>
    entries.filter((e) => e.worker_id === workerId && e.date === date);

  const assign = async (shiftId) => {
    if (!picker) return;
    try {
      await api.post("/schedule", {
        worker_id: picker.workerId,
        shift_id: shiftId,
        date: picker.date,
        route_id: pickerRoute || "",
      });
      setPicker(null);
      loadEntries();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unassign = async (id) => {
    await api.delete(`/schedule/${id}`);
    loadEntries();
  };

  const createShift = async (e) => {
    e.preventDefault();
    try {
      await api.post("/shifts", { ...shiftForm, type: "custom" });
      toast.success("Custom shift created");
      setShowShiftForm(false);
      setShiftForm({ name: "", start_time: "08:00", end_time: "16:00", color: "#FFDDC1" });
      api.get("/shifts").then((r) => setShifts(r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteShift = async (shiftId) => {
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success("Shift deleted");
      api.get("/shifts").then((r) => setShifts(r.data));
      loadEntries();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setEntryRoute = async (entryId, routeId) => {
    try {
      await api.put(`/schedule/${entryId}/route`, { route_id: routeId });
      loadEntries();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const label = `${weekDays[0].toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })} – ${weekDays[6].toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Planner</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Schedule</h1>
        </div>
        <button
          onClick={() => setShowShiftForm(!showShiftForm)}
          data-testid="add-shift-toggle"
          className={`bg-violet-300 border-2 border-black rounded-lg px-5 py-3 font-bold brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center gap-2 ${
            isAdmin ? "" : "hidden"
          }`}
        >
          <Plus weight="bold" size={20} /> Custom shift
        </button>
      </div>

      {showShiftForm && isAdmin && (
        <form
          onSubmit={createShift}
          data-testid="shift-form"
          className="bg-white border-2 border-black rounded-xl brutal-shadow p-6 grid sm:grid-cols-4 gap-4 items-end"
        >
          <div className="sm:col-span-1">
            <label className="text-xs font-bold uppercase tracking-widest">Name</label>
            <input
              required
              data-testid="shift-name"
              value={shiftForm.name}
              onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none"
              placeholder="Evening"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Start</label>
            <input
              type="time"
              data-testid="shift-start"
              value={shiftForm.start_time}
              onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">End</label>
            <input
              type="time"
              data-testid="shift-end"
              value={shiftForm.end_time}
              onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none"
            />
          </div>
          <div className="flex gap-2 items-end">
            <input
              type="color"
              value={shiftForm.color}
              onChange={(e) => setShiftForm({ ...shiftForm, color: e.target.value })}
              className="h-11 w-12 border-2 border-black rounded-lg"
            />
            <button
              type="submit"
              data-testid="shift-save"
              className="flex-1 bg-emerald-300 border-2 border-black rounded-lg px-4 py-2.5 font-bold brutal-shadow-sm"
            >
              Save
            </button>
          </div>
        </form>
      )}

      {/* shifts management */}
      <div className="bg-white border-2 border-black rounded-xl brutal-shadow p-4" data-testid="shifts-legend">
        <div className="flex items-center gap-2 mb-3">
          <Clock weight="bold" size={18} />
          <span className="font-display font-bold text-lg">Shifts</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {shifts.map((s) => (
            <span
              key={s.id}
              data-testid={`shift-chip-${s.id}`}
              className="inline-flex items-center gap-2 border-2 border-black rounded-md px-3 py-1.5 text-sm font-bold brutal-shadow-sm"
              style={{ backgroundColor: s.color }}
            >
              {s.name} · {s.start_time}–{s.end_time}
              {isAdmin && s.type === "custom" && (
                <button
                  onClick={() => deleteShift(s.id)}
                  data-testid={`shift-delete-${s.id}`}
                  title="Delete custom shift"
                  className="hover:text-red-700"
                >
                  <Trash weight="bold" size={14} />
                </button>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="week-prev"
            className="bg-white border-2 border-black rounded-lg p-2.5 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          >
            <CaretLeft weight="bold" size={18} />
          </button>
          <div className="bg-yellow-200 border-2 border-black rounded-lg px-5 py-2.5 font-display font-bold brutal-shadow-sm">
            {label}
          </div>
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="week-next"
            className="bg-white border-2 border-black rounded-lg p-2.5 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          >
            <CaretRight weight="bold" size={18} />
          </button>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            data-testid="week-today"
            className="bg-white border-2 border-black rounded-lg px-4 py-2.5 font-bold brutal-shadow-sm"
          >
            Today
          </button>
        </div>
        <div className="flex gap-1 bg-white border-2 border-black rounded-lg p-1 brutal-shadow-sm">
          {[
            { k: "week", label: "Week grid", icon: CalendarBlank },
            { k: "day", label: "Daily list", icon: ListBullets },
          ].map((v) => (
            <button
              key={v.k}
              onClick={() => setView(v.k)}
              data-testid={`view-${v.k}`}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-md font-bold text-sm transition-all ${
                view === v.k ? "bg-blue-500 text-white" : ""
              }`}
            >
              <v.icon weight="bold" size={16} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {workers.length === 0 && (
        <div className="bg-orange-200 border-2 border-black rounded-xl brutal-shadow p-6 font-bold">
          Add people first to start scheduling →
        </div>
      )}

      {/* WEEK GRID */}
      {view === "week" && workers.length > 0 && (
        <div className="overflow-x-auto bg-white border-2 border-black rounded-xl brutal-shadow">
          <table className="w-full border-collapse min-w-[800px]">
            <thead>
              <tr>
                <th className="border-b-2 border-r-2 border-black p-3 text-left text-xs font-bold uppercase tracking-widest w-40 sticky left-0 bg-white">
                  Person
                </th>
                {weekDays.map((d, i) => (
                  <th
                    key={i}
                    className="border-b-2 border-r-2 border-black p-3 text-center"
                  >
                    <div className="font-display font-bold">{DAYS[i]}</div>
                    <div className="text-xs font-medium text-slate-500">{d.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workers.map((w) => (
                <tr key={w.id}>
                  <td className="border-b-2 border-r-2 border-black p-3 sticky left-0 bg-white">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-md border-2 border-black flex items-center justify-center font-black text-sm"
                        style={{ backgroundColor: w.color }}
                      >
                        {w.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-bold text-sm">{w.name}</span>
                    </div>
                  </td>
                  {weekDays.map((d, i) => {
                    const date = fmt(d);
                    const items = cellEntries(w.id, date);
                    return (
                      <td
                        key={i}
                        className="border-b-2 border-r-2 border-black p-1.5 align-top min-w-[110px] h-24"
                        data-testid={`cell-${w.id}-${date}`}
                      >
                        <div className="space-y-1">
                          {items.map((e) => {
                            const s = shiftById(e.shift_id);
                            if (!s) return null;
                            const eRoute = routeById(e.route_id);
                            return (
                              <div
                                key={e.id}
                                className="group border-2 border-black rounded-md px-2 py-1 text-xs font-bold brutal-shadow-sm"
                                style={{ backgroundColor: s.color }}
                                data-testid={`entry-${e.id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span>
                                    {s.start_time}–{s.end_time}
                                  </span>
                                  <button
                                    onClick={() => unassign(e.id)}
                                    className={`opacity-0 group-hover:opacity-100 ${
                                      isAdmin ? "" : "hidden"
                                    }`}
                                  >
                                    <X weight="bold" size={12} />
                                  </button>
                                </div>
                                {eRoute && (
                                  <div
                                    className="mt-0.5 flex items-center gap-1 text-[10px] font-bold opacity-80"
                                    data-testid={`entry-route-${e.id}`}
                                  >
                                    <Path weight="bold" size={10} />
                                    <span className="truncate">{eRoute.name}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <button
                            onClick={() => {
                              setPickerRoute("");
                              setPicker({ workerId: w.id, date });
                            }}
                            data-testid={`add-${w.id}-${date}`}
                            className={`w-full border-2 border-dashed border-slate-300 rounded-md py-1 text-slate-400 hover:border-black hover:text-black transition-colors ${
                              isAdmin ? "" : "hidden"
                            }`}
                          >
                            <Plus weight="bold" size={14} className="mx-auto" />
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DAILY LIST */}
      {view === "day" && workers.length > 0 && (
        <div className="space-y-4">
          {weekDays.map((d, i) => {
            const date = fmt(d);
            const dayItems = entries.filter((e) => e.date === date);
            return (
              <div
                key={i}
                className="bg-white border-2 border-black rounded-xl brutal-shadow p-5"
                data-testid={`day-${date}`}
              >
                <h3 className="font-display text-xl font-bold mb-3">
                  {d.toLocaleDateString(undefined, {
                    weekday: "long",
                    day: "numeric",
                    month: "short",
                  })}
                </h3>
                {dayItems.length === 0 ? (
                  <p className="text-sm font-medium text-slate-400">No one scheduled.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {dayItems.map((e) => {
                      const w = workers.find((x) => x.id === e.worker_id);
                      const s = shiftById(e.shift_id);
                      if (!w || !s) return null;
                      return (
                        <div
                          key={e.id}
                          className="flex items-center gap-3 border-2 border-black rounded-lg p-3"
                        >
                          <div
                            className="w-10 h-10 rounded-lg border-2 border-black flex items-center justify-center font-black"
                            style={{ backgroundColor: w.color }}
                          >
                            {w.name?.[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="font-bold">{w.name}</div>
                            <div className="text-xs font-bold flex items-center gap-1 text-slate-600">
                              <Clock weight="bold" size={13} />
                              {s.name} · {s.start_time}–{s.end_time}
                            </div>
                            <div className="mt-1 flex items-center gap-1">
                              <Path weight="bold" size={13} className="text-blue-700" />
                              {isAdmin ? (
                                <select
                                  data-testid={`entry-route-select-${e.id}`}
                                  value={e.route_id || ""}
                                  onChange={(ev) => setEntryRoute(e.id, ev.target.value)}
                                  className="text-xs border-2 border-black rounded-md px-1.5 py-1 bg-white font-bold"
                                >
                                  <option value="">No route</option>
                                  {routes.map((r) => (
                                    <option key={r.id} value={r.id}>
                                      {r.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  className="text-xs font-bold text-blue-700"
                                  data-testid={`day-entry-route-${e.id}`}
                                >
                                  {routeById(e.route_id)?.name || "No route"}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => unassign(e.id)}
                            data-testid={`day-remove-${e.id}`}
                            className={isAdmin ? "" : "hidden"}
                          >
                            <X weight="bold" size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* shift picker modal */}
      {picker && isAdmin && (
        <div
          className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setPicker(null)}
        >
          <div
            className="bg-white border-2 border-black rounded-xl brutal-shadow-lg p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="shift-picker"
          >
            <h3 className="font-display text-xl font-bold mb-1">Assign a shift</h3>
            <p className="text-xs font-medium text-slate-500 mb-4">
              Choose a routing plan (optional), then pick the shift to assign.
            </p>
            <label className="text-xs font-bold uppercase tracking-widest">Routing plan</label>
            <select
              data-testid="picker-route-select"
              value={pickerRoute}
              onChange={(e) => setPickerRoute(e.target.value)}
              className="mt-1 mb-4 w-full border-2 border-black rounded-lg px-3 py-2.5 font-bold bg-white focus:outline-none"
            >
              <option value="">No route</option>
              {routes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <label className="text-xs font-bold uppercase tracking-widest">Shift</label>
            <div className="mt-1 space-y-2">
              {shifts.map((s) => (
                <button
                  key={s.id}
                  onClick={() => assign(s.id)}
                  data-testid={`pick-${s.id}`}
                  className="w-full border-2 border-black rounded-lg px-4 py-3 font-bold brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center justify-between"
                  style={{ backgroundColor: s.color }}
                >
                  <span>{s.name}</span>
                  <span className="font-mono text-sm">
                    {s.start_time}–{s.end_time}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setPicker(null)}
              className="mt-4 w-full border-2 border-black rounded-lg py-2 font-bold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
